import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ShareIcon from "@mui/icons-material/Share";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useFeatureFlagEnabled } from "../../common/posthog";
import { IndicatorBlock } from "../../components/IndicatorBlock";
import { getPerformanceLevel } from "../../types/artifact";
import { formatDate } from "../../utils/date";
import { logError } from "../../utils/error";
import ExplorerNavigator from "../explore/components/ExplorerNavigator";
import ShareDialog from "./components/ShareDialog";
import { getArtifactFromTabIndex, getTabIndexFromArtifact, TAB_INDEX } from "./tabs";
import useArtifactDetails from "./hooks/useArtifactDetails";
import ArtifactsTab from "./tabs/ArtifactsTab";
import PerformanceTab from "./tabs/PerformanceTab";
import PotentialRiskTab from "./tabs/PotentialRiskTab";
import RiskTab from "./tabs/RiskTab";
import SummaryTab from "./tabs/SummaryTab";
import TranscriptTab from "./tabs/TranscriptTab";
import { updateUrlParams } from "./utils";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface NavigationProps {
  currentIndex: number;
  totalCount: number;
  onPrevious: () => void;
  onNext: () => void;
}

interface ViewerProps {
  interactionId?: string;
  initialTab?: number;
  expandedAttribute?: string;
  onClose?: () => void;
  navigation?: NavigationProps;
}

// Types for call data
interface CallParty {
  id: string;
  type: string;
  isInternal?: boolean;
  connections?: Array<{
    leftAt: string;
    joinedAt: string;
    durationSec: number;
  }>;
  displayValue: string;
}

interface CallSummary {
  header: string;
  outcome: string;
}

interface CallEvent {
  isInternal: boolean;
  summary: CallSummary;
  parties: CallParty[];
  startedAt: string;
  duration: number;
  durationText: string;
}

interface CallHeaderProps {
  data: Record<string, any> | null;
}

const CallHeader = ({ data }: CallHeaderProps) => {
  const callEvent = data?.callDataRecord?.content?.callEvent as CallEvent;
  const callRecordingEvent = data?.callRecordingEvent?.content?.callEvent as CallEvent;
  const callTranscriptionEvent = data?.callTranscriptionRecord?.content?.callEvent as CallEvent;
  if (!callEvent && !callRecordingEvent && !callTranscriptionEvent) {
    return null;
  }

  const { summary, startedAt } = callEvent || callRecordingEvent || callTranscriptionEvent;
  const formattedDate = startedAt ? formatDate(new Date(startedAt), "dd MMMM yyyy @ h:mm a") : "";

  const header = summary?.header || "Call Details";
  const outcome = summary?.outcome || "";

  // Extract indicator values from the data
  const sentiment = data?.summary?.content?.sentiment;
  const relevance = data?.summary?.content?.relevance;
  const risk = data?.riskAnalysis?.content?.riskAnalysis?.riskAnalysis?.overallIndicator || "N/A";
  const performanceScore = data?.scorecard?.content?.scorecard?.overall_score;

  // Get call type directly from the database field
  const callType = callEvent?.isInternal ? "internal" : "external";
  const performanceLevel = getPerformanceLevel(performanceScore);

  return (
    <Box sx={{ p: 2, bgcolor: "#FFF", borderRadius: "4px 4px 0 0" }}>
      <Grid container spacing={2} justifyContent="space-between">
        <Grid size="grow">
          <Stack spacing={1}>
            <Typography variant="h6" component="h2">
              {header}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {[formattedDate && `Called ${formattedDate}`, outcome].filter(Boolean).join(". ")}
              </Typography>
              <Tooltip title={callType === "internal" ? "Internal" : "External"} placement="top" arrow>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    backgroundColor: callType === "internal" ? "#3f51b5" : "#4caf50",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "12px",
                    ml: 1
                  }}
                >
                  {callType === "internal" ? "I" : "E"}
                </Box>
              </Tooltip>
            </Stack>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 8, lg: 6 }}>
          <Box sx={{ p: 1, mr: 2, flexWrap: "wrap", gap: 2 }}>
            {/* Sentiment Indicator */}
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid size={{ xs: 6, md: 6 }}>
                {sentiment && (
                  <IndicatorBlock
                    title="Sentiment"
                    value={sentiment}
                    field="sentiment"
                  />
                )}
              </Grid>

              {/* Relevance Indicator */}
              <Grid size={{ xs: 6, md: 6 }}>
                {relevance && (
                  <IndicatorBlock
                    title="Relevance"
                    value={relevance}
                    field="relevance"
                  />
                )}
              </Grid>

              {/* Risk Indicator */}
              <Grid size={{ xs: 6, md: 6 }}>
                {/* {risk && ( */}
                <IndicatorBlock
                  title="Risk"
                  value={risk}
                  field="risk"
                />
                {/* )} */}
              </Grid>

              {/* Performance Indicator */}
              <Grid size={{ xs: 6, md: 6 }}>
                {/* {performanceScore && ( */}
                <IndicatorBlock
                  title="Performance"
                  value={performanceLevel}
                  field="performance"
                />
                {/* )} */}
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`artifact-tabpanel-${index}`}
      aria-labelledby={`artifact-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `artifact-tab-${index}`,
    "aria-controls": `artifact-tabpanel-${index}`
  };
};

export const Viewer = ({
  interactionId: propInteractionId,
  initialTab = TAB_INDEX.SUMMARY,
  expandedAttribute,
  onClose,
  navigation
}: ViewerProps) => {
  const [searchParams] = useSearchParams();
  const urlInteractionId = searchParams.get("interaction_id");
  const interactionId = propInteractionId || urlInteractionId;

  const artifactParam = searchParams.get("artifact");
  const timestampParam = searchParams.get("timestamp");
  const attributeParam = searchParams.get("attribute");

  const tabFromArtifact = getTabIndexFromArtifact(artifactParam);
  const effectiveInitialTab = artifactParam ? tabFromArtifact : initialTab;

  const [currentTab, setCurrentTab] = useState(effectiveInitialTab);
  const [currentExpandedAttribute, setCurrentExpandedAttribute] = useState<string | null>(
    attributeParam || expandedAttribute || null
  );

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const shareCallFeatureEnabled = useFeatureFlagEnabled("share-call-elements");
  const potentialRiskTabEnabled = useFeatureFlagEnabled("potential-risk-tab");

  const { data, isLoading, error, fetchData } = useArtifactDetails(interactionId);

  useEffect(() => {
    if (expandedAttribute && propInteractionId) {
      setCurrentTab(TAB_INDEX.SCORECARD);
      setCurrentExpandedAttribute(expandedAttribute);
    }
  }, [expandedAttribute, propInteractionId]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setCurrentExpandedAttribute(null);

    if (!propInteractionId) {
      const artifact = getArtifactFromTabIndex(newValue);
      updateUrlParams({
        artifact,
        attribute: null,
        timestamp: null
      });
    }
  };

  const handleAttributeChange = (attributeName: string | null) => {
    setCurrentExpandedAttribute(attributeName);
    if (!propInteractionId) {
      updateUrlParams({
        attribute: attributeName
      });
    }
  };

  const handleShareClick = () => {
    setShareDialogOpen(true);
  };

  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
  };

  const handleCopyLink = async () => {
    if (!interactionId) {
      return;
    }

    const url = `${window.location.origin}/viewer?interaction_id=${interactionId}`;

    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      logError("Failed to copy link", err);
    }
  };

  useEffect(() => {
    if (interactionId) {
      fetchData();
    }
  }, [interactionId, fetchData]);

  if (!interactionId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No interaction ID provided</Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Determine if we're in a panel or full page
  const isInPanel = Boolean(propInteractionId);
  const paddingValue = isInPanel ? 0 : 3;

  const handleOpenInNewTab = () => {
    if (interactionId) {
      window.open(`/viewer?interaction_id=${interactionId}`, "_blank");
    }
  };

  return (
    <Box sx={{ p: paddingValue }}>
      <Paper elevation={1}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
          }}
        >
          <Typography variant="h6" component="div">
            Interaction Details
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {isInPanel && navigation && (
              <ExplorerNavigator
                currentIndex={navigation.currentIndex}
                totalCount={navigation.totalCount}
                onPrevious={navigation.onPrevious}
                onNext={navigation.onNext}
              />
            )}
            <Box sx={{ display: "flex", gap: 1 }}>
              {shareCallFeatureEnabled && (
                <Tooltip title="Share call">
                  <IconButton
                    onClick={handleShareClick}
                    color="primary"
                    size="small"
                    aria-label="share"
                  >
                    <ShareIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={linkCopied ? "Link copied!" : "Copy link to clipboard"}>
                <IconButton
                  onClick={handleCopyLink}
                  color="primary"
                  size="small"
                  aria-label="copy link"
                >
                  <LinkIcon />
                </IconButton>
              </Tooltip>
              {isInPanel && (
                <>
                  <Tooltip title="Open in new tab">
                    <IconButton
                      onClick={handleOpenInNewTab}
                      color="primary"
                      size="small"
                      aria-label="open in new tab"
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </Tooltip>
                  {onClose && (
                    <Tooltip title="Close">
                      <IconButton
                        onClick={onClose}
                        color="primary"
                        size="small"
                        aria-label="close panel"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
        <CallHeader data={data} />
        <Divider />
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="artifact tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Summary" {...a11yProps(TAB_INDEX.SUMMARY)} />
            <Tab label="Performance Scorecard" {...a11yProps(TAB_INDEX.SCORECARD)} />
            <Tab label={potentialRiskTabEnabled ? "Potential Risk" : "Risk Analysis (Beta)"} {...a11yProps(TAB_INDEX.RISK)} />
            <Tab label="Transcript" {...a11yProps(TAB_INDEX.TRANSCRIPT)} />
            <Tab label="Artifacts" {...a11yProps(TAB_INDEX.RAW)} />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={TAB_INDEX.SUMMARY}>
          <SummaryTab data={data} isLoading={isLoading} />
        </TabPanel>

        <TabPanel value={currentTab} index={TAB_INDEX.SCORECARD}>
          <PerformanceTab
            data={data}
            isLoading={isLoading}
            expandedAttribute={currentExpandedAttribute}
            onAttributeChange={handleAttributeChange}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={TAB_INDEX.RISK}>
          {potentialRiskTabEnabled
            ? <PotentialRiskTab data={data} isLoading={isLoading} />
            : <RiskTab data={data} isLoading={isLoading} />
          }
        </TabPanel>

        <TabPanel value={currentTab} index={TAB_INDEX.TRANSCRIPT}>
          <TranscriptTab
            data={data}
            isLoading={isLoading}
            initialTimestamp={timestampParam}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={TAB_INDEX.RAW}>
          <ArtifactsTab data={data} isLoading={isLoading} />
        </TabPanel>
      </Paper>

      {shareCallFeatureEnabled && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={handleShareDialogClose}
          data={data}
          interactionId={interactionId}
        />
      )}
    </Box>
  );
};

export default Viewer;
